// ============================================================================
// FILE: frontend/src/components/SharePointFilePicker.jsx
// UPDATED: Now supports OneDrive, SharePoint Sites, AND Shared With Me
// ============================================================================

import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../api";

// mode="file" (default) picks a file and calls onFileSelected.
// mode="folder" picks a folder -- a folder row's Select, or "Select this
// folder" for the one being viewed -- and calls onFolderSelected. Both report
// driveId and a display path, because Graph needs (driveId, itemId) to address
// an item and a person needs the path to recognise it (master-file Settings).
export default function SharePointFilePicker({
  isOpen, onClose, onFileSelected, onFolderSelected, clientName,
  mode = "file", title, subtitle,
}) {
  const folderMode = mode === "folder";
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState([{ name: "Root", id: "root" }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // NEW: Tab state - 'onedrive', 'sharepoint', 'shared'
  const [activeTab, setActiveTab] = useState("onedrive");
  
  // NEW: For SharePoint sites navigation
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [drives, setDrives] = useState([]);
  const [selectedDrive, setSelectedDrive] = useState(null);

  useEffect(() => {
    if (isOpen) {
      checkAuthStatus();
    }
  }, [isOpen]);

  const checkAuthStatus = async () => {
    try {
      const response = await apiGet("/sharepoint/auth/status/");
      setIsAuthenticated(response.authenticated);
      if (response.authenticated) {
        loadContent();
      }
    } catch (e) {
      setIsAuthenticated(false);
    }
  };

  const handleAuthenticate = async () => {
    try {
      setIsAuthenticating(true);
      setError("");
      
      const response = await apiGet("/sharepoint/auth/url/");
      const authUrl = response.auth_url;
      
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        authUrl,
        "SharePoint Login",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      const pollInterval = setInterval(async () => {
        try {
          const status = await apiGet("/sharepoint/auth/status/");
          if (status.authenticated) {
            clearInterval(pollInterval);
            if (popup && !popup.closed) {
              popup.close();
            }
            setIsAuthenticated(true);
            setIsAuthenticating(false);
            loadContent();
          }
        } catch (e) {
          // Continue polling
        }
      }, 1000);

      const checkPopupClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollInterval);
          clearInterval(checkPopupClosed);
          setIsAuthenticating(false);
        }
      }, 500);

    } catch (e) {
      setError(`Authentication failed: ${e.message}`);
      setIsAuthenticating(false);
    }
  };

  // Load content based on active tab
  const loadContent = () => {
    setFiles([]);
    setError("");
    setCurrentPath([{ name: getTabName(activeTab), id: "root" }]);
    setSelectedSite(null);
    setSelectedDrive(null);
    
    if (activeTab === "onedrive") {
      loadFiles("root");
    } else if (activeTab === "sharepoint") {
      loadSites();
    } else if (activeTab === "shared") {
      loadSharedWithMe();
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
    setFiles([]);
    setSites([]);
    setDrives([]);
    setSelectedSite(null);
    setSelectedDrive(null);
    setCurrentPath([{ name: getTabName(tab), id: "root" }]);
    
    if (tab === "onedrive") {
      loadFiles("root");
    } else if (tab === "sharepoint") {
      loadSites();
    } else if (tab === "shared") {
      loadSharedWithMe();
    }
  };

  const getTabName = (tab) => {
    switch (tab) {
      case "onedrive": return "OneDrive";
      case "sharepoint": return "SharePoint Sites";
      case "shared": return "Shared With Me";
      default: return "Files";
    }
  };

  // Load OneDrive files
  const loadFiles = async (folderId, driveId = null) => {
    try {
      setLoading(true);
      setError("");
      
      let url = `/sharepoint/files/?folder_id=${folderId}`;
      if (driveId) {
        url += `&drive_id=${driveId}`;
      }
      
      const response = await apiGet(url);
      
      // Separate files and folders
      const allItems = response.files || [];
      setFiles(allItems);
      setLoading(false);
    } catch (e) {
      setError(`Failed to load files: ${e.message}`);
      setLoading(false);
    }
  };

  // Load SharePoint sites
  const loadSites = async () => {
    console.log("=== loadSites called ==="); // ADD THIS
    try {
      setLoading(true);
      setError("");

      const response = await apiGet("/sharepoint/sites/");
      console.log("=== Sites response ===", response); // ADD THIS
      setSites(response.sites || []);
      setFiles([]);
      setLoading(false);
    } catch (e) {
      console.error("=== loadSites ERROR ===", e); // ADD THIS
      setError(`Failed to load sites: ${e.message}`);
      setLoading(false);
    }
  };

  // Load drives for a SharePoint site
  const loadSiteDrives = async (site) => {
    console.log("=== loadSiteDrives called ===", site); // ADD THIS
    try {
      setLoading(true);
      setError("");
      setSelectedSite(site);
      setCurrentPath([
        { name: "SharePoint Sites", id: "root", type: "sites" },
        { name: site.name, id: site.id, type: "site" },
      ]);

      console.log(
        "=== Calling API: /sharepoint/sites/" + site.id + "/drives/ ==="
      ); // ADD THIS
      const response = await apiGet(`/sharepoint/sites/${site.id}/drives/`);
      console.log("=== API Response ===", response); // ADD THIS
      setDrives(response.drives || []);
      setSites([]);
      setFiles([]);
      setLoading(false);
    } catch (e) {
      console.error("=== loadSiteDrives ERROR ===", e); // ADD THIS
      setError(`Failed to load document libraries: ${e.message}`);
      setLoading(false);
    }
  };

  // Load files from a SharePoint drive
  const loadDriveFiles = async (drive, folderId = "root") => {
    try {
      setLoading(true);
      setError("");
      setSelectedDrive(drive);
      
      if (folderId === "root") {
        setCurrentPath([
          { name: "SharePoint Sites", id: "root", type: "sites" },
          { name: selectedSite.name, id: selectedSite.id, type: "site" },
          { name: drive.name, id: drive.id, type: "drive" }
        ]);
      }
      
      const response = await apiGet(`/sharepoint/files/?folder_id=${folderId}&drive_id=${drive.id}`);
      setFiles(response.files || []);
      setDrives([]);
      setLoading(false);
    } catch (e) {
      setError(`Failed to load files: ${e.message}`);
      setLoading(false);
    }
  };

  // Load files shared with me
  const loadSharedWithMe = async () => {
    try {
      setLoading(true);
      setError("");
      
      const response = await apiGet("/sharepoint/shared/");
      setFiles(response.files || []);
      setLoading(false);
    } catch (e) {
      setError(`Failed to load shared files: ${e.message}`);
      setLoading(false);
    }
  };

  // Handle folder click
  const handleFolderClick = (folder) => {
    if (activeTab === "sharepoint" && selectedDrive) {
      setCurrentPath([...currentPath, { name: folder.name, id: folder.id, type: "folder", webUrl: folder.webUrl }]);
      loadDriveFiles(selectedDrive, folder.id);
    } else if (activeTab === "onedrive") {
      setCurrentPath([...currentPath, { name: folder.name, id: folder.id, type: "folder", driveId: folder.driveId, webUrl: folder.webUrl }]);
      loadFiles(folder.id);
    } else if (activeTab === "shared" && folder.driveId) {
      // For shared folders, we need the driveId
      setCurrentPath([...currentPath, { name: folder.name, id: folder.id, type: "folder", driveId: folder.driveId, webUrl: folder.webUrl }]);
      loadFiles(folder.id, folder.driveId);
    }
  };

  // Handle breadcrumb click
  const handleBreadcrumbClick = (index) => {
    const clickedItem = currentPath[index];
    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    
    if (clickedItem.type === "sites" || index === 0) {
      if (activeTab === "sharepoint") {
        setSites([]);
        setDrives([]);
        setFiles([]);
        setSelectedSite(null);
        setSelectedDrive(null);
        loadSites();
      } else if (activeTab === "onedrive") {
        loadFiles("root");
      } else if (activeTab === "shared") {
        loadSharedWithMe();
      }
    } else if (clickedItem.type === "site") {
      setDrives([]);
      setFiles([]);
      setSelectedDrive(null);
      loadSiteDrives({ id: clickedItem.id, name: clickedItem.name });
    } else if (clickedItem.type === "drive") {
      setFiles([]);
      loadDriveFiles({ id: clickedItem.id, name: clickedItem.name }, "root");
    } else {
      // It's a folder
      if (selectedDrive) {
        loadDriveFiles(selectedDrive, clickedItem.id);
      } else if (clickedItem.driveId) {
        loadFiles(clickedItem.id, clickedItem.driveId);
      } else {
        loadFiles(clickedItem.id);
      }
    }
  };

  // The breadcrumb as a readable path, without the tab's own root crumb.
  const pathNames = () => currentPath.slice(1).map((p) => p.name);

  const driveIdHere = () => {
    if (selectedDrive) return selectedDrive.id;
    for (let i = currentPath.length - 1; i >= 0; i -= 1) {
      if (currentPath[i].driveId) return currentPath[i].driveId;
    }
    return null;
  };

  const handleFileSelect = (file) => {
    if (folderMode) return;
    onFileSelected({
      id: file.id,
      name: file.name,
      webUrl: file.webUrl,
      size: file.size,
      modifiedDateTime: file.lastModified,
      driveId: file.driveId || driveIdHere(),
      path: [...pathNames(), file.name].join(" / "),
    });
  };

  const selectFolder = (folder) => {
    onFolderSelected && onFolderSelected({
      id: folder.id,
      name: folder.name,
      webUrl: folder.webUrl || "",
      driveId: folder.driveId || driveIdHere(),
      path: [...pathNames(), folder.name].join(" / "),
    });
  };

  // "Select this folder": the folder being viewed, once inside a library.
  const here = currentPath[currentPath.length - 1];
  const canSelectHere = folderMode && (here?.type === "drive" || here?.type === "folder") && !!driveIdHere();
  const selectHere = () => {
    if (!canSelectHere) return;
    onFolderSelected && onFolderSelected({
      // A library's top level: the server resolves "root" to a real item id.
      id: here.type === "drive" ? "root" : here.id,
      name: here.name,
      webUrl: here.webUrl || "",
      driveId: driveIdHere(),
      path: pathNames().join(" / "),
    });
  };

  // Filter files and folders based on search
  const filteredFiles = files.filter(f => 
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSites = sites.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDrives = drives.filter(d =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 12,
          width: "90%",
          maxWidth: 900,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                color: "#111827",
              }}
            >
              {title || "Attach from SharePoint / OneDrive"}
            </h3>
            <p style={{ margin: "4px 0 0 0", fontSize: 13, color: "#6b7280" }}>
              {subtitle || `For ${clientName}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
              color: "#6b7280",
              padding: "4px 8px",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: 0 }}>
          {!isAuthenticated ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📎</div>
              <h4
                style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 600 }}
              >
                Connect to Microsoft 365
              </h4>
              <p
                style={{ margin: "0 0 24px 0", color: "#6b7280", fontSize: 14 }}
              >
                Sign in to access OneDrive, SharePoint sites, and files shared
                with you
              </p>
              <button
                onClick={handleAuthenticate}
                disabled={isAuthenticating}
                style={{
                  padding: "12px 24px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: isAuthenticating ? "not-allowed" : "pointer",
                  opacity: isAuthenticating ? 0.6 : 1,
                }}
              >
                {isAuthenticating
                  ? "Connecting..."
                  : "Connect to Microsoft 365"}
              </button>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #e5e7eb",
                  background: "#f9fafb",
                }}
              >
                <TabButton
                  active={activeTab === "onedrive"}
                  onClick={() => handleTabChange("onedrive")}
                  icon="💾"
                  label="OneDrive"
                />
                <TabButton
                  active={activeTab === "sharepoint"}
                  onClick={() => handleTabChange("sharepoint")}
                  icon="🏢"
                  label="SharePoint Sites"
                />
                <TabButton
                  active={activeTab === "shared"}
                  onClick={() => handleTabChange("shared")}
                  icon="👥"
                  label="Shared With Me"
                />
              </div>

              <div style={{ padding: 20 }}>
                {/* Breadcrumb Navigation */}
                <div
                  style={{
                    marginBottom: 16,
                    padding: "10px 12px",
                    background: "#f9fafb",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 4,
                  }}
                >
                  {currentPath.map((pathItem, index) => (
                    <React.Fragment key={`${pathItem.id}-${index}`}>
                      <button
                        onClick={() => handleBreadcrumbClick(index)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color:
                            index === currentPath.length - 1
                              ? "#111827"
                              : "#2563eb",
                          cursor:
                            index === currentPath.length - 1
                              ? "default"
                              : "pointer",
                          fontSize: 13,
                          fontWeight:
                            index === currentPath.length - 1 ? 600 : 400,
                          padding: "4px 8px",
                          borderRadius: 4,
                        }}
                      >
                        {pathItem.name}
                      </button>
                      {index < currentPath.length - 1 && (
                        <span style={{ color: "#9ca3af" }}>›</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Search */}
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    fontSize: 14,
                    marginBottom: 16,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />

                {error && (
                  <div
                    style={{
                      background: "#fee2e2",
                      border: "1px solid #fecaca",
                      color: "#991b1b",
                      padding: 12,
                      borderRadius: 8,
                      marginBottom: 16,
                      fontSize: 13,
                    }}
                  >
                    {error}
                  </div>
                )}

                {loading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: 40,
                      color: "#6b7280",
                    }}
                  >
                    <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                    Loading...
                  </div>
                ) : (
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      overflow: "hidden",
                      maxHeight: 400,
                      overflowY: "auto",
                    }}
                  >
                    {/* SharePoint Sites List */}
                    {activeTab === "sharepoint" &&
                      filteredSites.length > 0 &&
                      filteredSites.map((site) => (
                        <ItemRow
                          key={site.id}
                          icon="🏢"
                          name={site.name}
                          subtitle={site.description || site.webUrl}
                          onClick={() => {
                            console.log("=== Site clicked ===", site); // ADD THIS
                            loadSiteDrives(site);
                          }}
                          showArrow
                        />
                      ))}

                    {/* Document Libraries (Drives) List */}
                    {filteredDrives.length > 0 &&
                      filteredDrives.map((drive) => (
                        <ItemRow
                          key={drive.id}
                          icon="📚"
                          name={drive.name}
                          subtitle="Document Library"
                          onClick={() => loadDriveFiles(drive)}
                          showArrow
                        />
                      ))}

                    {/* Files and Folders */}
                    {filteredFiles.map((item) =>
                      item.isFolder ? (
                        <ItemRow
                          key={item.id}
                          icon="📁"
                          name={item.name}
                          subtitle="Folder"
                          onClick={() => handleFolderClick(item)}
                          showArrow
                          actionButton={folderMode ? (
                            <SelectButton
                              label={`Select folder ${item.name}`}
                              onClick={(e) => { e.stopPropagation(); selectFolder(item); }}
                            />
                          ) : null}
                        />
                      ) : (
                        <ItemRow
                          key={item.id}
                          icon={getFileIcon(item.name)}
                          name={item.name}
                          subtitle={`${formatFileSize(item.size)}${
                            item.sharedBy ? ` • Shared by ${item.sharedBy}` : ""
                          }`}
                          onClick={() => handleFileSelect(item)}
                          actionButton={folderMode ? <span /> :
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileSelect(item);
                              }}
                              style={{
                                padding: "6px 12px",
                                background: "#2563eb",
                                color: "white",
                                border: "none",
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 500,
                                cursor: "pointer",
                              }}
                            >
                              Select
                            </button>
                          }
                        />
                      )
                    )}

                    {/* Empty State */}
                    {filteredSites.length === 0 &&
                      filteredDrives.length === 0 &&
                      filteredFiles.length === 0 && (
                        <div
                          style={{
                            padding: 40,
                            textAlign: "center",
                            color: "#6b7280",
                          }}
                        >
                          {searchQuery
                            ? "No items match your search"
                            : activeTab === "shared"
                            ? "No files have been shared with you"
                            : "No items found"}
                        </div>
                      )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
          }}
        >
          {folderMode && (
            <button
              type="button"
              onClick={selectHere}
              disabled={!canSelectHere}
              title={canSelectHere ? undefined : "Open a document library or folder first"}
              style={{
                padding: "10px 20px",
                background: canSelectHere ? "#2563eb" : "#93c5fd",
                color: "white",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: canSelectHere ? "pointer" : "not-allowed",
              }}
            >
              Select this folder
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              background: "white",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              color: "#374151",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function SelectButton({ label, onClick }) {
  return (
    <button
      aria-label={label}
      onClick={onClick}
      style={{
        padding: "6px 12px",
        background: "#2563eb",
        color: "white",
        border: "none",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      Select
    </button>
  );
}

// Tab Button Component
function TabButton({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "12px 20px",
        background: active ? "white" : "transparent",
        border: "none",
        borderBottom: active ? "2px solid #2563eb" : "2px solid transparent",
        color: active ? "#2563eb" : "#6b7280",
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
        transition: "all 0.15s ease"
      }}
    >
      <span>{icon}</span>
      {label}
    </button>
  );
}

// Item Row Component
function ItemRow({ icon, name, subtitle, onClick, showArrow, actionButton }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "12px 16px",
        borderBottom: "1px solid #f3f4f6",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 12,
        transition: "background-color 0.15s ease"
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
      onMouseLeave={(e) => e.currentTarget.style.background = "white"}
    >
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ 
          fontSize: 14, 
          fontWeight: 500, 
          color: "#111827",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
          {name}
        </div>
        {subtitle && (
          <div style={{ 
            fontSize: 12, 
            color: "#6b7280", 
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
            {subtitle}
          </div>
        )}
      </div>
      {actionButton || (showArrow && <span style={{ color: "#9ca3af" }}>›</span>)}
    </div>
  );
}

// Helper functions
function getFileIcon(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  const iconMap = {
    'doc': '📝', 'docx': '📝',
    'xls': '📊', 'xlsx': '📊',
    'ppt': '📽️', 'pptx': '📽️',
    'pdf': '📄',
    'jpg': '🖼️', 'jpeg': '🖼️', 'png': '🖼️', 'gif': '🖼️',
    'zip': '🗜️', 'rar': '🗜️',
    'txt': '📃',
    'csv': '📊'
  };
  return iconMap[ext] || '📄';
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 10) / 10 + ' ' + sizes[i];
}